import { Position } from "@xyflow/react";
import { CV } from "@/config/canvasSizing";

interface SimpleNode {
  position: { x: number; y: number };
  width?: number | null;
  height?: number | null;
}

function nodeCenter(n: SimpleNode) {
  const w = n.width ?? CV.node.defaultW;
  const h = n.height ?? CV.node.defaultH;
  return { x: n.position.x + w / 2, y: n.position.y + h / 2 };
}

function boundaryIntersect(cx: number, cy: number, hw: number, hh: number, dx: number, dy: number) {
  const sx = Math.abs(dx) > 0 ? hw / Math.abs(dx) : Infinity;
  const sy = Math.abs(dy) > 0 ? hh / Math.abs(dy) : Infinity;
  const s = Math.min(sx, sy);
  return { x: cx + dx * s, y: cy + dy * s };
}

export function edgePosition(cx: number, cy: number, px: number, py: number): Position {
  const dx = px - cx;
  const dy = py - cy;
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? Position.Right : Position.Left;
  return dy > 0 ? Position.Bottom : Position.Top;
}

interface HitNode extends SimpleNode {
  id: string;
  // ReactFlow populates `measured` with the node's real rendered size after
  // layout; it's authoritative when a node's DOM size differs from the width/
  // height we set (min-widths, content-driven growth). Prefer it for hit-testing.
  measured?: { width?: number | null; height?: number | null };
}

export type EndpointDrop =
  | { type: "reconnect"; nodeId: string }
  | { type: "detach" };

// Decide what dropping a dragged edge endpoint at `point` (flow coords) means:
// if it lands inside a node → reconnect that end to it; otherwise → detach into
// a free arrow. Nodes are hit-tested topmost-first (last in the array renders on
// top). `excludeId` skips the node at the fixed end so an edge can't collapse
// onto a self-loop — that drop detaches instead.
export function resolveEndpointDrop(
  point: { x: number; y: number },
  nodes: HitNode[],
  excludeId?: string,
): EndpointDrop {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    if (n.id === excludeId) continue;
    const w = n.measured?.width  ?? n.width  ?? CV.node.defaultW;
    const h = n.measured?.height ?? n.height ?? CV.node.defaultH;
    if (
      point.x >= n.position.x && point.x <= n.position.x + w &&
      point.y >= n.position.y && point.y <= n.position.y + h
    ) {
      return { type: "reconnect", nodeId: n.id };
    }
  }
  return { type: "detach" };
}

export function getFloatingEdgePoints(src: SimpleNode, tgt: SimpleNode) {
  const srcW = src.width ?? CV.node.defaultW;
  const srcH = src.height ?? CV.node.defaultH;
  const tgtW = tgt.width ?? CV.node.defaultW;
  const tgtH = tgt.height ?? CV.node.defaultH;

  const { x: sx, y: sy } = nodeCenter(src);
  const { x: tx, y: ty } = nodeCenter(tgt);
  const dx = tx - sx;
  const dy = ty - sy;

  const srcPt = boundaryIntersect(sx, sy, srcW / 2, srcH / 2, dx, dy);
  const tgtPt = boundaryIntersect(tx, ty, tgtW / 2, tgtH / 2, -dx, -dy);

  return {
    sourceX: srcPt.x,
    sourceY: srcPt.y,
    sourcePosition: edgePosition(sx, sy, srcPt.x, srcPt.y),
    targetX: tgtPt.x,
    targetY: tgtPt.y,
    targetPosition: edgePosition(tx, ty, tgtPt.x, tgtPt.y),
  };
}
